import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getSpiritualFathers, getFatherMembers, toggleLeader, importMembers } from '../../services/api';
import { FiUser, FiUsers, FiSearch, FiX, FiTrash2, FiChevronDown, FiChevronRight, FiDownload, FiUpload, FiFileText } from 'react-icons/fi';
import { downloadCSV, parseCSV, readFileAsText } from '../../utils/csv';
import { useNavigate } from 'react-router-dom';
import Toast from '../../components/Toast';
import DependentCard from '../../components/DependentCard';
import PhoneLink from '../../components/PhoneLink';

const currentYear = new Date().getFullYear();

const statusPill = { member: 'pill-green', inactive: 'pill-red', transferred: 'pill-blue', changed_religion: 'pill-gray', deceased: 'pill-gray', suspended: 'pill-red' };

const SpiritualFathers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fathers, setFathers] = useState([]);
  const [selectedFather, setSelectedFather] = useState(null);
  const [fatherMembers, setFatherMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [expandedMember, setExpandedMember] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const loadFathers = () => getSpiritualFathers().then(r => setFathers(Array.isArray(r.data) ? r.data : [])).catch(() => {});

  useEffect(() => { loadFathers(); }, []);

  const selectFather = (f) => {
    setSelectedFather(f);
    setMemberSearch('');
    setExpandedMember(null);
    getFatherMembers(f.id).then(r => setFatherMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };

  const handleRemoveLeader = async (father) => {
    if (father.member_count > 0) {
      showToast(`Cannot remove — ${father.first_name} ${father.last_name} still has ${father.member_count} member${father.member_count > 1 ? 's' : ''} assigned. Reassign them first.`, 'error');
      return;
    }
    if (!window.confirm(`Remove ${father.first_name} ${father.last_name} as a spiritual leader?`)) return;
    try {
      await toggleLeader(father.id);
      if (selectedFather?.id === father.id) { setSelectedFather(null); setFatherMembers([]); }
      showToast('Leader removed');
      loadFathers();
    } catch (e) { showToast('Failed', 'error'); }
  };

  const today = new Date().toISOString().slice(0, 10);

  const CSV_COLS = [
    { key: 'title' }, { key: 'first_name' }, { key: 'middle_name' }, { key: 'last_name' },
    { key: 'christian_name' }, { key: 'gender' }, { key: 'birth_year' }, { key: 'phone' },
    { key: 'email' }, { key: 'address' }, { key: 'department' }, { key: 'zone' },
    { key: 'is_spiritual_father', get: () => 'true' },
  ].map(c => ({ key: c.key, label: c.key, ...c }));

  const handleExport = () => {
    if (fathers.length === 0) { showToast('No spiritual fathers to export', 'error'); return; }
    downloadCSV(`spiritual-fathers-${today}.csv`, CSV_COLS, fathers);
  };

  const handleSampleCSV = () => {
    const sample = [{
      title: 'Qeshi', first_name: 'Teklom', middle_name: 'Welday', last_name: 'Tesfay',
      christian_name: '', gender: 'male', birth_year: 1965, phone: '+260977000111',
      email: '', address: 'Lusaka', department: 'Spiritual Care', zone: 'Zone A',
      is_spiritual_father: 'true',
    }];
    downloadCSV('spiritual-fathers-sample.csv', CSV_COLS, sample);
  };

  const fileInputRef = React.useRef(null);
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const rows = parseCSV(text).map(r => ({ ...r, is_spiritual_father: 'true' }));
      if (rows.length === 0) { showToast('CSV is empty', 'error'); return; }
      if (!window.confirm(`Import ${rows.length} spiritual father${rows.length === 1 ? '' : 's'}?`)) return;
      const r = await importMembers(rows);
      const msg = `Imported ${r.data.imported} of ${r.data.total}` + (r.data.errors?.length ? ` (${r.data.errors.length} skipped)` : '');
      showToast(msg, r.data.errors?.length ? 'error' : 'success');
      if (r.data.errors?.length) console.warn('Import errors:', r.data.errors);
      loadFathers();
    } catch (e) { showToast(e.response?.data?.error || 'Import failed', 'error'); }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1>{t('nav.spiritualFathers')}</h1>
          <p>{fathers.length} leaders · {fathers.reduce((s, f) => s + f.member_count, 0)} members assigned</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={handleSampleCSV}><FiFileText /> Sample CSV</button>
          <button className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()}><FiUpload /> Import</button>
          <button className="btn btn-secondary btn-sm" onClick={handleExport}><FiDownload /> Export</button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleImportFile} />
        </div>
      </div>

      {fathers.length === 0 ? (
        <div className="card">
          <div className="empty-state" style={{ padding: '60px 24px' }}>
            <div className="empty-icon"><FiUser /></div>
            <h3>No spiritual leaders yet</h3>
            <p>Mark a member as a spiritual father from their profile to get started</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '280px 1fr', gap: 20 }}>
          {/* Left: Leaders */}
          <div className="card" style={{ height: 'fit-content' }}>
            <div className="card-header" style={{ fontSize: 13, fontWeight: 700 }}>Leaders ({fathers.length})</div>
            {fathers.map(f => (
              <div key={f.id} style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', background: selectedFather?.id === f.id ? '#eef2ff' : 'transparent', transition: 'background 0.15s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ cursor: 'pointer', flex: 1 }} onClick={() => selectFather(f)}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {f.title ? `${f.title} ` : ''}{f.first_name}{f.middle_name ? ` ${f.middle_name}` : ''}{f.last_name ? ` ${f.last_name}` : ''}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                      {f.member_count} {f.member_count === 1 ? 'member' : 'members'}
                      {f.phone ? ` · ${f.phone}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span className="pill pill-purple" style={{ fontSize: 11 }}>{f.member_count}</span>
                    <button className="btn-icon danger" title="Remove leader" onClick={() => handleRemoveLeader(f)} style={{ fontSize: 13 }}><FiTrash2 /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right: Members under selected leader */}
          <div>
            {!selectedFather ? (
              <div className="card">
                <div className="empty-state" style={{ padding: '60px 24px' }}>
                  <div className="empty-icon"><FiUsers /></div>
                  <h3>Select a leader</h3>
                  <p>Click any leader to see their assigned members</p>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>
                    {selectedFather.title ? `${selectedFather.title} ` : ''}{selectedFather.first_name}{selectedFather.middle_name ? ` ${selectedFather.middle_name}` : ''}{selectedFather.last_name ? ` ${selectedFather.last_name}` : ''}'s Members
                  </strong>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span className="pill pill-purple">{fatherMembers.length} members</span>
                    <button className="btn btn-sm btn-primary" onClick={() => navigate(`/members/add?father_id=${selectedFather.id}`)}>+ Add Member</button>
                  </div>
                </div>

                {/* Search bar */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                  <div className="search-input-container" style={{ margin: 0 }}>
                    <FiSearch style={{ color: '#9ca3af' }} />
                    <input
                      placeholder="Search member by name or phone..."
                      value={memberSearch}
                      onChange={e => setMemberSearch(e.target.value)}
                    />
                  </div>
                </div>

                {(() => {
                  const filtered = fatherMembers.filter(m =>
                    `${m.first_name} ${m.middle_name || ''} ${m.last_name}`.toLowerCase().includes(memberSearch.toLowerCase()) ||
                    (m.phone || '').includes(memberSearch)
                  );

                  if (fatherMembers.length === 0) return (
                    <div className="empty-state" style={{ padding: '48px 24px' }}>
                      <div className="empty-icon"><FiUsers /></div>
                      <h3>No members assigned yet</h3>
                      <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate(`/members/add?father_id=${selectedFather.id}`)}>+ Add First Member</button>
                    </div>
                  );

                  if (filtered.length === 0) return (
                    <div className="empty-state" style={{ padding: '32px 24px' }}>
                      <p>No members match "{memberSearch}"</p>
                    </div>
                  );

                  return (
                    <div style={{ overflowX: 'auto' }}>
                      <table>
                        <thead>
                          <tr><th></th><th>#</th><th>Name</th><th>Phone</th><th>Status</th><th>Dependents</th><th>Last Follow-up</th><th>Next Follow-up</th><th>Follow-up Status</th></tr>
                        </thead>
                        <tbody>
                          {filtered.map((m, i) => {
                            const overdue = m.next_followup && m.next_followup < today;
                            const isExpanded = expandedMember === m.id;
                            const hasDeps = m.dependents && m.dependents.length > 0;
                            return (
                              <>
                                <tr key={m.id} style={{ background: m.membership_status === 'inactive' ? '#fffbeb' : hasDeps ? '#f5f3ff' : 'transparent', cursor: hasDeps ? 'pointer' : 'default', borderLeft: hasDeps ? '3px solid #7c3aed' : '3px solid transparent' }}
                                  onClick={() => hasDeps && setExpandedMember(isExpanded ? null : m.id)}>
                                  <td style={{ width: 28, color: '#9ca3af' }}>
                                    {hasDeps ? (isExpanded ? <FiChevronDown /> : <FiChevronRight />) : null}
                                  </td>
                                  <td>{i + 1}</td>
                                  <td>
                                    <strong>{m.title ? `${m.title} ` : ''}{m.first_name} {m.middle_name || ''} {m.last_name}</strong>
                                    {m.membership_status === 'inactive' && <span className="pill pill-red" style={{ marginLeft: 6, fontSize: 10 }}>Inactive</span>}
                                  </td>
                                  <td onClick={e => e.stopPropagation()}><PhoneLink phone={m.phone} whatsapp={m.whatsapp} /></td>
                                  <td><span className={`pill ${statusPill[m.membership_status]}`}>{m.membership_status}</span></td>
                                  <td>
                                    {hasDeps
                                      ? <span className="pill pill-blue" style={{ cursor: 'pointer' }}>{m.dependent_count} {m.dependent_count === 1 ? 'dependent' : 'dependents'}</span>
                                      : <span style={{ color: '#9ca3af', fontSize: 12 }}>None</span>}
                                  </td>
                                  <td>{m.last_followup || <span style={{ color: '#9ca3af' }}>Never</span>}</td>
                                  <td style={{ color: overdue ? '#dc2626' : 'inherit', fontWeight: overdue ? 700 : 'normal' }}>
                                    {m.next_followup || '—'}
                                    {overdue && <span className="pill pill-red" style={{ marginLeft: 4, fontSize: 10 }}>Overdue</span>}
                                  </td>
                                  <td>
                                    {m.last_status
                                      ? <span className={`pill ${m.last_status === 'good' ? 'pill-green' : m.last_status === 'needs_attention' ? 'pill-yellow' : m.last_status === 'critical' ? 'pill-red' : 'pill-gray'}`}>{m.last_status.replace('_', ' ')}</span>
                                      : <span style={{ color: '#9ca3af', fontSize: 12 }}>No record</span>}
                                  </td>
                                </tr>
                                {isExpanded && hasDeps && (
                                  <tr key={`${m.id}-deps`}>
                                    <td colSpan={9} style={{ padding: 0, background: '#f8fafc' }}>
                                      <div style={{ padding: '10px 48px 14px' }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                          Dependents under {m.first_name} {m.last_name}
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                          {m.dependents.map(d => <DependentCard key={d.id} d={d} />)}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default SpiritualFathers;
